import type { LngLat } from "./geo/types.ts";
import type { OsmElement } from "./osm.ts";

type ExternalAppId =
  | "google-maps"
  | "apple-maps"
  | "openstreetmap"
  | "id-editor";

export type ExternalAppsTarget =
  | {
    kind: "coordinates";
    lngLat: LngLat;
    zoom: number;
  }
  | {
    kind: "poi";
    lngLat: LngLat;
    label?: string;
    addressLines: string[];
    osmElement: OsmElement;
  };

export interface ExternalAppLink {
  id: ExternalAppId;
  label: string;
  href: string;
}

export function externalAppLinks(
  target: ExternalAppsTarget,
): ExternalAppLink[] {
  return [
    {
      id: "google-maps",
      label: "Google Maps",
      href: googleMapsUrl(target),
    },
    {
      id: "apple-maps",
      label: "Apple Maps",
      href: appleMapsUrl(target),
    },
    {
      id: "openstreetmap",
      label: "OpenStreetMap",
      href: openStreetMapUrl(target),
    },
    {
      id: "id-editor",
      label: "iD editor",
      href: idEditorUrl(target),
    },
  ];
}

function googleMapsUrl(target: ExternalAppsTarget): string {
  const params = new URLSearchParams({
    api: "1",
    query: placeQuery(target),
  });
  return `https://www.google.com/maps/search/?${params}`;
}

function appleMapsUrl(target: ExternalAppsTarget): string {
  const params = new URLSearchParams({
    coordinate: latitudeLongitude(target.lngLat),
  });
  if (target.kind === "poi") {
    if (target.label) params.set("name", target.label);
    const address = target.addressLines.join(", ");
    if (address) params.set("address", address);
  }
  return `https://maps.apple.com/place?${params}`;
}

function openStreetMapUrl(target: ExternalAppsTarget): string {
  if (target.kind === "poi") {
    return `https://www.openstreetmap.org/${target.osmElement.type}/${target.osmElement.id}`;
  }

  const [lng, lat] = target.lngLat;
  return `https://www.openstreetmap.org/?mlat=${lat.toFixed(6)}&mlon=${
    lng.toFixed(6)
  }#map=${Math.round(target.zoom)}/${lat.toFixed(6)}/${lng.toFixed(6)}`;
}

function idEditorUrl(target: ExternalAppsTarget): string {
  const url = new URL("https://www.openstreetmap.org/edit");
  url.searchParams.set("editor", "id");

  if (target.kind === "poi") {
    url.searchParams.set(
      target.osmElement.type,
      String(target.osmElement.id),
    );
    return url.toString();
  }

  const [lng, lat] = target.lngLat;
  url.hash = `map=${Math.round(target.zoom)}/${lat.toFixed(6)}/${
    lng.toFixed(6)
  }`;
  return url.toString();
}

function placeQuery(target: ExternalAppsTarget): string {
  if (target.kind === "poi") {
    const address = target.addressLines.join(", ");
    return [target.label, address].filter(Boolean).join(", ") ||
      latitudeLongitude(target.lngLat);
  }
  return latitudeLongitude(target.lngLat);
}

function latitudeLongitude([lng, lat]: LngLat): string {
  return `${lat.toFixed(6)},${lng.toFixed(6)}`;
}
